import { Link } from 'react-router-dom';
import PublicLayout, { SEOHead } from '../../components/PublicLayout';
import { Button } from '../../components/ui/button';
import { Lock, Shield, Eye, BarChart3, Building2, CheckCircle, Globe } from 'lucide-react';

export default function SecurityPage() {
  return (
    <PublicLayout>
      <SEOHead title="Security & Privacy | Enterprise-Grade Interpreter Services | Interpreter Services" description="Hosted AI architecture. No public model training on your data. Configurable controls for enterprise and government environments." canonical="https://interpreterservices.us/security" />

      <section className="bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-6">Security & Privacy</h1>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto">Built for organizations that operate in regulated, high-trust environments. Privacy-conscious architecture from the ground up.</p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-6 py-16">
        <section className="mb-16">
          <h2 className="text-2xl font-bold mb-6">Hosted AI Architecture</h2>
          <p className="text-slate-700 leading-relaxed mb-4">Interpreter Services uses a hosted AI architecture. Your interpreting sessions are processed through infrastructure that is managed and monitored, with enterprise-grade security controls applied throughout the data lifecycle.</p>
          <p className="text-slate-700 leading-relaxed">This architecture is designed to meet the expectations of government agencies, healthcare organizations, legal firms, and law enforcement agencies that require control over how their data is processed and stored.</p>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold mb-6">Your Data Is Not Used for Public Model Training</h2>
          <p className="text-slate-700 leading-relaxed mb-4">Session data from Interpreter Services is not used to train public AI models. Your conversations, audio recordings, and transcripts remain within the boundaries of your organization's configured data retention policies.</p>
          <p className="text-slate-700 leading-relaxed">This is a critical distinction for organizations operating in healthcare (HIPAA), legal (attorney-client privilege), law enforcement, and government environments where data exposure carries significant risk.</p>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold mb-6">Enterprise Controls</h2>
          <div className="grid sm:grid-cols-2 gap-6">
            {[
              { icon: Eye, title: 'Configurable Transcript Visibility', desc: 'Control who can access session transcripts. Set visibility at the organization, department, or individual level.' },
              { icon: BarChart3, title: 'Audit-Ready Session Controls', desc: 'Every session is logged with timestamps, language codes, interpreter type (AI or human interpreters), and duration for compliance reporting.' },
              { icon: Lock, title: 'Data Retention Policies', desc: 'Configure how long session data is stored. Set automatic deletion schedules that align with your organization\'s policies.' },
              { icon: Building2, title: 'Policy-Based Escalation', desc: 'Set rules for when sessions must escalate to human interpreters based on language, context, or organizational policy.' },
              { icon: Globe, title: 'Jurisdiction-Aware Matching', desc: 'Configure interpreter matching to align with jurisdictional requirements for human escalation sessions.' },
              { icon: Shield, title: 'Access Controls', desc: 'Role-based access controls for administrators, users, and reporting personnel. SSO integration available for enterprise deployments.' },
            ].map(c => (
              <div key={c.title} className="flex items-start gap-4 p-5 bg-slate-50 rounded-lg border">
                <c.icon className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                <div><h3 className="font-bold text-sm mb-1">{c.title}</h3><p className="text-sm text-slate-600">{c.desc}</p></div>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold mb-6">Alignment for Privacy-Conscious Organizations</h2>
          <p className="text-slate-700 leading-relaxed mb-6">Interpreter Services is designed to support the privacy and security requirements of organizations operating in sensitive environments. While we continue to pursue formal certifications, our architecture and controls are built with the following frameworks in mind:</p>
          <ul className="space-y-3">
            {[
              'HIPAA alignment for healthcare interpreting sessions',
              'Support for attorney-client privilege protections in legal interpreting',
              'Title VI compliance support for government agencies',
              'Configurable controls for law enforcement and corrections environments',
              'Data residency considerations for multinational deployments',
            ].map(item => (
              <li key={item} className="flex items-start gap-3 text-sm text-slate-700"><CheckCircle className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />{item}</li>
            ))}
          </ul>
        </section>
      </div>

      <section className="py-16 bg-slate-900 text-white text-center">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold mb-4">Questions About Security?</h2>
          <p className="text-slate-400 mb-8">Our team can walk you through our security architecture, controls, and compliance alignment.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact"><Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white px-10">Contact Sales</Button></Link>
            <Link to="/contact"><Button size="lg" variant="outline" className="border-slate-500 text-white hover:bg-white/10 px-10">Book Demo</Button></Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
